import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
  Alert,
  Divider,
} from "@mui/material";

const RECURRENCE = ["daily", "weekly", "monthly"];

function getErrMsg(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err?.data?.msg) return err.data.msg;
  if (err?.data?.message) return err.data.message;
  if (err?.error) return err.error;
  if (err?.status) return `Request failed (${err.status})`;
  try {
    return JSON.stringify(err);
  } catch {
    return "Request failed";
  }
}

// datetime-local expects: "YYYY-MM-DDTHH:mm"
function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function CalendarEventModal({
  open,
  onClose,
  mode = "create", // "create" | "edit"
  initialData = null,
  onSubmit, // async (payload) => void
  isLoading = false,
}) {
  const [form, setForm] = useState({
    name: "",
    title: "",
    message: "",
    from: "",
    to: "",
    recurrence: "daily",
  });

  const [errMsg, setErrMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");

  useEffect(() => {
    setErrMsg("");
    setOkMsg("");

    if (mode === "edit" && initialData) {
      setForm({
        name: initialData?.name || "",
        title: initialData?.title || "",
        message: initialData?.message || "",
        from: toLocalInputValue(initialData?.from),
        to: toLocalInputValue(initialData?.to),
        recurrence: initialData?.recurrence || "daily",
      });
    } else {
      setForm({
        name: "",
        title: "",
        message: "",
        from: "",
        to: "",
        recurrence: "daily",
      });
    }
  }, [mode, initialData, open]);

  const canSave = useMemo(() => {
    if (!form.title?.trim()) return false;
    if (!form.from || !form.to) return false;

    const fromT = new Date(form.from).getTime();
    const toT = new Date(form.to).getTime();

    if (Number.isNaN(fromT) || Number.isNaN(toT)) return false;
    if (fromT > toT) return false;

    return true;
  }, [form]);

  const handleChange = (k) => (e) => {
    setErrMsg("");
    setOkMsg("");
    setForm((p) => ({ ...p, [k]: e.target.value }));
  };

  const handleSave = async () => {
    setErrMsg("");
    setOkMsg("");

    if (!canSave) {
      setErrMsg("Please fill Title + From + To (From must be <= To).");
      return;
    }

    try {
      const payload = {
        name: form.name?.trim() || "",
        title: form.title.trim(),
        message: form.message?.trim() || "",
        from: new Date(form.from).toISOString(),
        to: new Date(form.to).toISOString(),
        recurrence: form.recurrence,
      };

      await onSubmit?.(payload);

      setOkMsg(mode === "edit" ? "Updated successfully ✅" : "Saved successfully ✅");

      // create ke baad reset (edit me keep optional)
      if (mode !== "edit") {
        setForm({
          name: "",
          title: "",
          message: "",
          from: "",
          to: "",
          recurrence: "daily",
        });
      }

      // close after small UX? -> directly close
      onClose?.();
    } catch (err) {
      console.error("Calendar submit failed:", err);
      setErrMsg(getErrMsg(err));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {mode === "edit" ? "Edit Calendar" : "New Calendar"}
      </DialogTitle>

      <DialogContent>
        <Divider sx={{ mb: 2 }} />

        {!!errMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errMsg}
          </Alert>
        )}

        {!!okMsg && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {okMsg}
          </Alert>
        )}

        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 800, mb: 0.7 }}>Name</Typography>
              <TextField
                fullWidth
                value={form.name}
                onChange={handleChange("name")}
                placeholder="e.g. Reminder Group"
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 800, mb: 0.7 }}>Type</Typography>
              <TextField fullWidth select value="simple" disabled>
                <MenuItem value="simple">simple</MenuItem>
              </TextField>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 800, mb: 0.7 }}>From</Typography>
              <TextField
                fullWidth
                type="datetime-local"
                value={form.from}
                onChange={handleChange("from")}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 800, mb: 0.7 }}>To</Typography>
              <TextField
                fullWidth
                type="datetime-local"
                value={form.to}
                onChange={handleChange("to")}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Stack>

          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.7 }}>Recurrence</Typography>
            <TextField
              fullWidth
              select
              value={form.recurrence}
              onChange={handleChange("recurrence")}
            >
              {RECURRENCE.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.7 }}>Title</Typography>
            <TextField
              fullWidth
              value={form.title}
              onChange={handleChange("title")}
              placeholder="Popup title"
            />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.7 }}>Message</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={form.message}
              onChange={handleChange("message")}
              placeholder="Popup message"
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || isLoading}
          sx={{ fontWeight: 900, px: 3 }}
        >
          {isLoading ? "SAVING..." : "SAVE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
